import { useQuery } from '@tanstack/react-query';
import {
  Users,
  FileQuestion,
  BookOpen,
  CreditCard,
  TrendingUp,
  Activity,
  type LucideIcon,
} from 'lucide-react';
import api from '../lib/api';

interface DashboardData {
  totalUsers: number;
  newUsersThisMonth: number;
  totalQuestions: number;
  totalMockTests: number;
  totalAttempts: number;
  pendingPayments: number;
  activeSubscriptions: number;
}

function StatCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: LucideIcon;
  label: string;
  value: number | string;
  color: string;
}) {
  return (
    <div className="card flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { data, isLoading } = useQuery<DashboardData>({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const res = await api.get('/admin/dashboard');
      return res.data.data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-500 mt-1">Overview of your EPS Korean Prep platform</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Users"
          value={data?.totalUsers ?? 0}
          color="bg-primary-500"
        />
        <StatCard
          icon={TrendingUp}
          label="New This Month"
          value={data?.newUsersThisMonth ?? 0}
          color="bg-success-500"
        />
        <StatCard
          icon={FileQuestion}
          label="Questions"
          value={data?.totalQuestions ?? 0}
          color="bg-warning-500"
        />
        <StatCard
          icon={BookOpen}
          label="Mock Tests"
          value={data?.totalMockTests ?? 0}
          color="bg-purple-500"
        />
        <StatCard
          icon={Activity}
          label="Total Attempts"
          value={data?.totalAttempts ?? 0}
          color="bg-indigo-500"
        />
        <StatCard
          icon={CreditCard}
          label="Pending Payments"
          value={data?.pendingPayments ?? 0}
          color="bg-danger-500"
        />
        <StatCard
          icon={Users}
          label="Active Subscriptions"
          value={data?.activeSubscriptions ?? 0}
          color="bg-teal-500"
        />
      </div>
    </div>
  );
}
